import { and, eq, gte, sql } from "drizzle-orm";
import { drizzle, type DrizzleD1Database } from "drizzle-orm/d1";
import { Hono } from "hono";

import { dailyLocks, eventPicks, minesRounds, scores } from "./schema.js";
import {
  OUTCOME_WEIGHTS,
  TOTAL_WEIGHT,
  type OutcomeKind,
  type OutcomeWeight,
} from "./odds.js";
import {
  GAMES,
  isGameId,
  isValidValue,
  sanitizeHandle,
  type GameId,
} from "./validate.js";
import {
  CRASH_MAX,
  CRASH_MIN,
  GROWTH,
  deriveCrashFromR,
  multiplierAt,
  timeToReach,
  toScoreValue,
} from "./crash.js";
import {
  MINES_MAX_PICKS,
  deriveMines,
  minesMultAfter,
  minesToScoreValue,
} from "./mines.js";
import { isValidTarget, limboRoll, limboToScoreValue } from "./limbo.js";
import { plinkoDrop, plinkoToScoreValue } from "./plinko.js";
import {
  DRAW_SLOTS,
  drawEndsAt,
  drawOutcome,
  drawPicksLocked,
  drawRoundAt,
  ROCKET_MAX_FLIGHT_S,
  rocketLaunchAt,
  rocketPhase,
  rocketRoundAt,
} from "./rounds.js";

export interface GameApiEnv {
  DB: D1Database;
  /** Optional KV for rate limiting; falls back to a permissive no-op if unbound. */
  RATE_LIMIT?: KVNamespace;
  /** Optional funnel analytics (plays, shares, reply clicks); no-op if unbound. */
  EVENTS?: AnalyticsEngineDataset;
  /** HMAC secret for the crash game's provably-fair crash-point derivation. Falls back
   *  to a dev constant on local hosts only; on *.karrtopelka.com a missing secret is a
   *  hard error (a public seed would let anyone precompute crash points). */
  CRASH_SEED?: string;
}

type Db = DrizzleD1Database<Record<string, never>>;

interface Vars {
  db: Db;
}

const LEADERBOARD_LIMIT = 25;
const SCORE_RATE_WINDOW_S = 10; // min seconds between accepted submissions per anonId
/** Wheel wedge count (flavor only). Exported so the client wheel renders the same N. */
export const WHEEL_SEGMENTS = 8;

export interface TopEntry {
  handle: string;
  value: number;
  createdAt: Date;
}

/**
 * The current #1 for a game (best score across players). Used by the OG renderer to put
 * the leaderboard leader on the dynamic preview card. Returns null on an empty board.
 * Same best-per-player aggregate as /api/leaderboard, limited to 1.
 */
export async function topEntry(db: Db, game: GameId): Promise<TopEntry | null> {
  const dir = GAMES[game].dir;
  const bestValue =
    dir === "asc" ? sql<number>`min(${scores.value})` : sql<number>`max(${scores.value})`;
  const order =
    dir === "asc" ? sql`min(${scores.value}) asc` : sql`max(${scores.value}) desc`;

  const [row] = await db
    .select({
      handle: scores.handle,
      value: bestValue,
      createdAt: scores.createdAt,
    })
    .from(scores)
    .where(eq(scores.game, game))
    .groupBy(scores.anonId)
    .orderBy(order)
    .limit(1);

  return row ?? null;
}

export const router = new Hono<{ Bindings: GameApiEnv; Variables: Vars }>().basePath(
  "/api",
);

router.use("*", async (c, next) => {
  c.set("db", drizzle(c.env.DB));
  await next();
});

router.get("/health", async (c) => {
  // Proves D1 is reachable from the Worker — the M0 gate check.
  const row = await c.env.DB.prepare("select 1 as ok").first<{ ok: number }>();
  return c.json({ ok: row?.ok === 1, ts: Math.floor(Date.now() / 1000) });
});

// --- Funnel analytics (M2 instrumentation) ---
// One Analytics Engine dataset for every app. Index = game (sampling key);
// blobs = [event, anonId, extra]; doubles = [value]. Never throws, never blocks.

export function trackEvent(
  env: GameApiEnv,
  game: string,
  event: string,
  anonId = "",
  value = 0,
  extra = "",
): void {
  try {
    env.EVENTS?.writeDataPoint({
      indexes: [game],
      blobs: [event, anonId, extra],
      doubles: [value],
    });
  } catch (e) {
    console.error("trackEvent failed:", e);
  }
}

/** Events the client beacon may write. Server-only events (score_submit, spin, …) are
 *  written directly by their handlers and are NOT spoofable through /api/e. */
const BEACON_EVENTS = new Set([
  "page_view",
  "play_start",
  "play_done",
  "card_ready",
  "share_done",
  "link_copy",
]);

/** Client beacon (navigator.sendBeacon / fetch keepalive). Fire-and-forget. */
router.post("/e", async (c) => {
  let body: unknown;
  try {
    body = await c.req.json();
  } catch {
    return c.json({ ok: false }, 400);
  }
  if (typeof body !== "object" || body === null) return c.json({ ok: false }, 400);
  const { event, game, anonId, value, extra } = body as Record<string, unknown>;
  if (typeof event !== "string" || !BEACON_EVENTS.has(event)) {
    return c.json({ ok: false }, 400);
  }
  if (typeof game !== "string" || !isGameId(game)) return c.json({ ok: false }, 400);
  const anon = typeof anonId === "string" ? anonId.slice(0, 64) : "";
  const v = typeof value === "number" && Number.isFinite(value) ? value : 0;
  const x = typeof extra === "string" ? extra.slice(0, 64) : "";
  trackEvent(c.env, game, event, anon, v, x);
  return c.json({ ok: true }, 202);
});

/**
 * GET /r — the reply-link target ("<game>.karrtopelka.com/r" goes in the first reply on
 * X). Counts the click-through, then bounces to the game. Bot fetches (X's preview
 * crawler etc.) are tagged so human clicks can be counted separately. Mounted in each
 * app's Worker via run_worker_first, OUTSIDE /api (it's a user-facing URL).
 */
export function replyRedirect(req: Request, env: GameApiEnv, game: string): Response {
  const ua = req.headers.get("user-agent") ?? "";
  const isBot = /bot|crawler|spider|preview|external|facebookexternalhit/i.test(ua);
  trackEvent(env, game, "reply_click", "", 0, isBot ? "bot" : "human");
  return new Response(null, { status: 302, headers: { location: "/" } });
}

router.get("/leaderboard", async (c) => {
  const game = c.req.query("game") ?? "";
  if (!isGameId(game)) return c.json({ error: "unknown game" }, 400);
  // "today" = the daily race (UTC day): fresh competition every midnight, a reason to
  // come back even when the all-time board has calcified.
  const range = c.req.query("range") === "today" ? "today" : "all";

  const dir = GAMES[game].dir;
  const db = c.get("db");

  // One row per player (their BEST score), not every attempt — otherwise a player who
  // plays N times appears N times. SQLite returns the handle/createdAt from the row
  // that owns the min/max value when a bare column accompanies that aggregate.
  const bestValue =
    dir === "asc" ? sql<number>`min(${scores.value})` : sql<number>`max(${scores.value})`;
  const order =
    dir === "asc" ? sql`min(${scores.value}) asc` : sql`max(${scores.value}) desc`;

  const dayStart = new Date(new Date().setUTCHours(0, 0, 0, 0));
  const where =
    range === "today"
      ? and(eq(scores.game, game), gte(scores.createdAt, dayStart))
      : eq(scores.game, game);

  const rows = await db
    .select({
      handle: scores.handle,
      value: bestValue,
      createdAt: scores.createdAt,
    })
    .from(scores)
    .where(where)
    .groupBy(scores.anonId)
    .orderBy(order)
    .limit(LEADERBOARD_LIMIT);

  return c.json({ game, dir, range, entries: rows });
});

router.post("/score", async (c) => {
  let body: unknown;
  try {
    body = await c.req.json();
  } catch {
    return c.json({ error: "bad json" }, 400);
  }
  if (typeof body !== "object" || body === null) {
    return c.json({ error: "bad body" }, 400);
  }

  const { game, value, handle, anonId } = body as Record<string, unknown>;

  if (typeof game !== "string" || !isGameId(game)) {
    return c.json({ error: "unknown game" }, 400);
  }
  if (typeof value !== "number" || !isValidValue(game, value)) {
    return c.json({ error: "value out of bounds" }, 422);
  }
  const cleanHandle = sanitizeHandle(handle);
  if (cleanHandle === null) return c.json({ error: "bad handle" }, 400);
  if (typeof anonId !== "string" || anonId.length < 8 || anonId.length > 64) {
    return c.json({ error: "bad anonId" }, 400);
  }

  const limited = await rateLimited(c.env, anonId, game);
  if (limited) return c.json({ error: "rate limited" }, 429);

  const db = c.get("db");
  await db.insert(scores).values({ game, handle: cleanHandle, anonId, value });

  const { rank, total, percentile } = await rankFor(db, game, value);
  trackEvent(c.env, game, "score_submit", anonId, value);
  return c.json({ ok: true, rank, total, percentile }, 201);
});

// --- Gamble outcomes (wheel): push-your-luck run, no daily gate ---
// The streak is the brag; a spin gambles it. The whole "again" chain is resolved
// server-side in one request (un-gameable). The weights/odds live in ./odds.ts (a leaf
// module the client can import without pulling the server bundle).

export interface Outcome {
  kind: OutcomeKind;
  /** Streak after this step (for "again" it equals the running streak — unchanged). */
  streakAfter: number;
  /** Needle stop on the dial, 0..100. Flavor only. */
  gaugePercent: number;
  /** Center label for this result. */
  label: string;
}

type OutcomeSpec = OutcomeWeight;
const OUTCOMES = OUTCOME_WEIGHTS;
const MAX_REROLLS = 10; // defensive cap on an all-"again" chain
const WHEEL_MAX = GAMES.wheel.max; // clamp multipliers to the validate ceiling

/** Flat weighted pick — identical every spin, independent of streak/history. */
function pickOutcome(): OutcomeSpec {
  let r = Math.random() * TOTAL_WEIGHT;
  for (const o of OUTCOMES) {
    r -= o.weight;
    if (r < 0) return o;
  }
  return OUTCOMES[0]!; // unreachable; satisfies the type
}

/** Apply a TERMINAL outcome to the CURRENT run streak. Multipliers act on the streak
 *  (floored at 1 so a multiplier off a 0-streak still pays). "again" re-rolls only. */
function applyTerminal(kind: OutcomeKind, base: number): number {
  switch (kind) {
    case "x2":
      return Math.max(1, base) * 2;
    case "jackpot":
      return Math.max(1, base) * 3;
    case "bust":
      return 0;
    case "nothing":
      return base; // whiff — streak unchanged, run continues
    case "+1":
    case "again": // not terminal, but keep the switch exhaustive
      return base + 1;
  }
}

/** Resolve the full spin: re-roll "again" until a terminal outcome. Returns the chain
 *  (last entry terminal) + the final clamped streak. */
function resolveSpin(base: number): { outcomes: Outcome[]; streak: number } {
  const outcomes: Outcome[] = [];
  for (let i = 0; i < MAX_REROLLS; i++) {
    const o = pickOutcome();
    if (o.kind === "again") {
      outcomes.push({ kind: "again", streakAfter: base, gaugePercent: o.gaugePercent, label: o.label });
      continue;
    }
    const streak = Math.min(applyTerminal(o.kind, base), WHEEL_MAX);
    outcomes.push({ kind: o.kind, streakAfter: streak, gaugePercent: o.gaugePercent, label: o.label });
    return { outcomes, streak };
  }
  // Cap hit (all "again"): force a terminal +1.
  const streak = Math.min(base + 1, WHEEL_MAX);
  outcomes.push({ kind: "+1", streakAfter: streak, gaugePercent: 50, label: "+1" });
  return { outcomes, streak };
}

interface RunState {
  /** Current run streak (0 if no run yet). */
  streak: number;
}

/** Current run state (no mutation). anonId via query — GET has no body, no auth. */
router.get("/run", async (c) => {
  const game = c.req.query("game") ?? "";
  if (!isGameId(game)) return c.json({ error: "unknown game" }, 400);
  const anonId = c.req.query("anonId") ?? "";
  if (anonId.length < 8 || anonId.length > 64) {
    return c.json({ error: "bad anonId" }, 400);
  }

  const db = c.get("db");
  const [row] = await db
    .select({ streak: dailyLocks.streak })
    .from(dailyLocks)
    .where(and(eq(dailyLocks.anonId, anonId), eq(dailyLocks.game, game)));

  const state: RunState = { streak: row?.streak ?? 0 };
  return c.json(state);
});

/** The spin: gamble the current run streak (push-your-luck, no daily gate). */
router.post("/spin", async (c) => {
  let body: unknown;
  try {
    body = await c.req.json();
  } catch {
    return c.json({ error: "bad json" }, 400);
  }
  if (typeof body !== "object" || body === null) {
    return c.json({ error: "bad body" }, 400);
  }
  const { game, anonId, handle } = body as Record<string, unknown>;
  if (typeof game !== "string" || !isGameId(game)) {
    return c.json({ error: "unknown game" }, 400);
  }
  const cleanHandle = sanitizeHandle(handle);
  if (cleanHandle === null) return c.json({ error: "bad handle" }, 400);
  if (typeof anonId !== "string" || anonId.length < 8 || anonId.length > 64) {
    return c.json({ error: "bad anonId" }, 400);
  }

  const db = c.get("db");
  const now = Date.now();

  // Push-your-luck: no daily gate. The streak is the CURRENT run; each spin gambles it.
  // daily_locks holds the live run streak (lastPlay = last-spin time, not a lock).
  const [row] = await db
    .select({ streak: dailyLocks.streak })
    .from(dailyLocks)
    .where(and(eq(dailyLocks.anonId, anonId), eq(dailyLocks.game, game)));

  const current = row?.streak ?? 0;

  // Resolve the "again" chain server-side; the terminal outcome modifies the run streak
  // (+1 grows, x2/x3 multiply, bust → 0 ends the run).
  const { outcomes, streak } = resolveSpin(current);

  const nowDate = new Date(now);
  if (row) {
    await db
      .update(dailyLocks)
      .set({ lastPlay: nowDate, streak })
      .where(and(eq(dailyLocks.anonId, anonId), eq(dailyLocks.game, game)));
  } else {
    await db
      .insert(dailyLocks)
      .values({ anonId, game, lastPlay: nowDate, streak });
  }
  // Brag ledger: leaderboard/OG/percentile read scores. Write the post-spin streak; the
  // board uses max(value) per player, so it tracks each player's PEAK run — a later bust
  // (0) never lowers their best.
  await db.insert(scores).values({ game, handle: cleanHandle, anonId, value: streak });

  const { rank, total, percentile } = await rankFor(db, game, streak);
  trackEvent(c.env, game, "spin", anonId, streak);
  return c.json({ outcomes, streak, rank, total, percentile }, 201);
});

/** Start a crash round. Returns the round + the (server-committed) crash point for the
 *  climb animation. The crash point is real (HMAC over a secret seed) and re-derived at
 *  cashout, so revealing it here doesn't let a cheater claim a cashout above it. */
router.post("/crash/start", async (c) => {
  let body: unknown;
  try {
    body = await c.req.json();
  } catch {
    return c.json({ error: "bad json" }, 400);
  }
  if (typeof body !== "object" || body === null) {
    return c.json({ error: "bad body" }, 400);
  }
  const { game, anonId } = body as Record<string, unknown>;
  if (game !== "crash") return c.json({ error: "unknown game" }, 400);
  if (typeof anonId !== "string" || anonId.length < 8 || anonId.length > 64) {
    return c.json({ error: "bad anonId" }, 400);
  }

  if (await rateLimited(c.env, anonId, "crash", CRASH_START_WINDOW_S, "rlstart")) {
    return c.json({ error: "slow down" }, 429);
  }

  const seed = crashSeed(c.env, c.req.url);
  const roundId = crypto.randomUUID();
  const startedAt = Date.now();
  const crashPoint = await deriveCrash(seed, roundId);
  const sig = await hmacHex(seed, `${roundId}.${startedAt}`);

  trackEvent(c.env, "crash", "crash_start", anonId);
  return c.json({ roundId, startedAt, sig, crashPoint, growth: GROWTH }, 201);
});

/** Resolve a cashout. Server re-derives the crash point + bounds the awarded multiplier
 *  by its own elapsed clock, so the result is honest regardless of the client. */
router.post("/crash/cashout", async (c) => {
  let body: unknown;
  try {
    body = await c.req.json();
  } catch {
    return c.json({ error: "bad json" }, 400);
  }
  if (typeof body !== "object" || body === null) {
    return c.json({ error: "bad body" }, 400);
  }
  const { game, anonId, handle, roundId, startedAt, sig, cashoutMultiplier } =
    body as Record<string, unknown>;

  if (game !== "crash") return c.json({ error: "unknown game" }, 400);
  const cleanHandle = sanitizeHandle(handle);
  if (cleanHandle === null) return c.json({ error: "bad handle" }, 400);
  if (typeof anonId !== "string" || anonId.length < 8 || anonId.length > 64) {
    return c.json({ error: "bad anonId" }, 400);
  }
  if (typeof roundId !== "string" || roundId.length < 8 || roundId.length > 64) {
    return c.json({ error: "bad round" }, 400);
  }
  if (typeof startedAt !== "number" || !Number.isFinite(startedAt)) {
    return c.json({ error: "bad start" }, 400);
  }
  if (typeof sig !== "string") return c.json({ error: "bad sig" }, 400);
  if (typeof cashoutMultiplier !== "number" || !Number.isFinite(cashoutMultiplier)) {
    return c.json({ error: "bad cashout" }, 400);
  }

  const seed = crashSeed(c.env, c.req.url);
  const expectSig = await hmacHex(seed, `${roundId}.${startedAt}`);
  if (!safeEqual(sig, expectSig)) return c.json({ error: "bad sig" }, 400);

  const now = Date.now();
  if (startedAt > now + CRASH_CLOCK_SKEW_MS || now - startedAt > CRASH_MAX_ROUND_MS) {
    return c.json({ error: "stale round" }, 400);
  }

  const crashPoint = await deriveCrash(seed, roundId);
  const serverMult = multiplierAt((now - startedAt) / 1000);
  // Trust the client's displayed number only up to what the server clock allows — this
  // removes the latency penalty for honest players while capping a cheater's claim.
  const award = Math.min(Math.max(1, cashoutMultiplier), serverMult);

  if (award >= crashPoint) {
    // The round had already crashed by the time they cashed out.
    trackEvent(c.env, "crash", "crash_bust", anonId, toScoreValue(crashPoint));
    return c.json({ won: false, crashPoint });
  }

  const value = Math.min(Math.max(toScoreValue(award), CRASH_MIN), CRASH_MAX);
  const db = c.get("db");
  await db.insert(scores).values({ game: "crash", handle: cleanHandle, anonId, value });
  const { rank, total, percentile } = await rankFor(db, "crash", value);
  trackEvent(c.env, "crash", "crash_cashout", anonId, value);

  return c.json(
    { won: true, multiplier: award, crashPoint, value, rank, total, percentile },
    201,
  );
});

// --- Mines: D1-stateful rounds (the ledger is the anti-probe) ---
// The board is derived from HMAC(seed, roundId) — committed before the first pick —
// and every reveal appends to one irreversible history row. No sigs needed client-side;
// the row IS the truth.

const MINES_MAX_ROUND_MS = 10 * 60_000; // abandon a board after 10 min

function parsePicks(csv: string): number[] {
  return csv === "" ? [] : csv.split(",").map((s) => Number.parseInt(s, 10));
}

async function minesBoard(env: GameApiEnv, reqUrl: string, roundId: string) {
  const digest = await hmacHex(crashSeed(env, reqUrl), `mines.${roundId}`);
  return deriveMines(digest);
}

router.post("/mines/start", async (c) => {
  let body: unknown;
  try {
    body = await c.req.json();
  } catch {
    return c.json({ error: "bad json" }, 400);
  }
  const { anonId } = (body ?? {}) as Record<string, unknown>;
  if (typeof anonId !== "string" || anonId.length < 8 || anonId.length > 64) {
    return c.json({ error: "bad anonId" }, 400);
  }
  if (await rateLimited(c.env, anonId, "mines", CRASH_START_WINDOW_S, "rlstart")) {
    return c.json({ error: "slow down" }, 429);
  }

  const roundId = crypto.randomUUID();
  const db = c.get("db");
  await db
    .insert(minesRounds)
    .values({ roundId, anonId, startedAt: new Date() });

  trackEvent(c.env, "mines", "mines_start", anonId);
  return c.json({ roundId }, 201);
});

router.post("/mines/reveal", async (c) => {
  let body: unknown;
  try {
    body = await c.req.json();
  } catch {
    return c.json({ error: "bad json" }, 400);
  }
  const { anonId, roundId, pick } = (body ?? {}) as Record<string, unknown>;
  if (typeof anonId !== "string" || anonId.length < 8 || anonId.length > 64) {
    return c.json({ error: "bad anonId" }, 400);
  }
  if (typeof roundId !== "string" || roundId.length < 8 || roundId.length > 64) {
    return c.json({ error: "bad round" }, 400);
  }
  if (typeof pick !== "number" || !Number.isInteger(pick) || pick < 0 || pick > 24) {
    return c.json({ error: "bad pick" }, 400);
  }

  const db = c.get("db");
  const [row] = await db
    .select()
    .from(minesRounds)
    .where(and(eq(minesRounds.roundId, roundId), eq(minesRounds.anonId, anonId)));
  if (!row) return c.json({ error: "unknown round" }, 404);
  if (row.dead === 1 || row.cashed === 1) return c.json({ error: "round over" }, 409);
  if (Date.now() - row.startedAt.getTime() > MINES_MAX_ROUND_MS) {
    return c.json({ error: "stale round" }, 400);
  }

  const picks = parsePicks(row.picks);
  if (picks.includes(pick)) return c.json({ error: "already revealed" }, 409);
  if (picks.length >= MINES_MAX_PICKS) return c.json({ error: "board cleared" }, 409);

  const mines = await minesBoard(c.env, c.req.url, roundId);
  if (mines.includes(pick)) {
    await db
      .update(minesRounds)
      .set({ dead: 1 })
      .where(eq(minesRounds.roundId, roundId));
    trackEvent(c.env, "mines", "mines_bust", anonId, picks.length);
    // Reveal the whole board — the post-mortem is half the fun.
    return c.json({ hit: true, mines, picks });
  }

  const nextPicks = [...picks, pick];
  await db
    .update(minesRounds)
    .set({ picks: nextPicks.join(",") })
    .where(eq(minesRounds.roundId, roundId));

  const mult = minesMultAfter(nextPicks.length);
  const cleared = nextPicks.length >= MINES_MAX_PICKS;
  return c.json({ hit: false, mult, count: nextPicks.length, cleared });
});

router.post("/mines/cashout", async (c) => {
  let body: unknown;
  try {
    body = await c.req.json();
  } catch {
    return c.json({ error: "bad json" }, 400);
  }
  const { anonId, handle, roundId } = (body ?? {}) as Record<string, unknown>;
  if (typeof anonId !== "string" || anonId.length < 8 || anonId.length > 64) {
    return c.json({ error: "bad anonId" }, 400);
  }
  const cleanHandle = sanitizeHandle(handle);
  if (cleanHandle === null) return c.json({ error: "bad handle" }, 400);
  if (typeof roundId !== "string" || roundId.length < 8 || roundId.length > 64) {
    return c.json({ error: "bad round" }, 400);
  }

  const db = c.get("db");
  const [row] = await db
    .select()
    .from(minesRounds)
    .where(and(eq(minesRounds.roundId, roundId), eq(minesRounds.anonId, anonId)));
  if (!row) return c.json({ error: "unknown round" }, 404);
  if (row.dead === 1 || row.cashed === 1) return c.json({ error: "round over" }, 409);

  const k = parsePicks(row.picks).length;
  if (k < 1) return c.json({ error: "nothing to cash" }, 400);

  await db
    .update(minesRounds)
    .set({ cashed: 1 })
    .where(eq(minesRounds.roundId, roundId));

  const mult = minesMultAfter(k);
  const value = minesToScoreValue(mult);
  await db.insert(scores).values({ game: "mines", handle: cleanHandle, anonId, value });
  const { rank, total, percentile } = await rankFor(db, "mines", value);
  trackEvent(c.env, "mines", "mines_cashout", anonId, value);
  return c.json({ mult, gems: k, value, rank, total, percentile }, 201);
});

// --- Limbo: one-shot nerve check, zero state ---

router.post("/limbo/roll", async (c) => {
  let body: unknown;
  try {
    body = await c.req.json();
  } catch {
    return c.json({ error: "bad json" }, 400);
  }
  const { anonId, handle, target } = (body ?? {}) as Record<string, unknown>;
  if (typeof anonId !== "string" || anonId.length < 8 || anonId.length > 64) {
    return c.json({ error: "bad anonId" }, 400);
  }
  const cleanHandle = sanitizeHandle(handle);
  if (cleanHandle === null) return c.json({ error: "bad handle" }, 400);
  if (typeof target !== "number" || !isValidTarget(target)) {
    return c.json({ error: "bad target" }, 400);
  }
  if (await rateLimited(c.env, anonId, "limbo", CRASH_START_WINDOW_S, "rlstart")) {
    return c.json({ error: "slow down" }, 429);
  }

  // Fresh roll per request off the same provably-fair curve as crash. No commitment
  // needed: there are no mid-round decisions for a client to game.
  const digest = await hmacHex(crashSeed(c.env, c.req.url), `limbo.${crypto.randomUUID()}`);
  const r = Number.parseInt(digest.slice(0, 13), 16) / 2 ** 52;
  const roll = limboRoll(r);
  const won = roll >= target;

  if (!won) {
    trackEvent(c.env, "limbo", "limbo_lose", anonId, Math.round(target * 100));
    return c.json({ won: false, roll, target });
  }

  const value = limboToScoreValue(target);
  const db = c.get("db");
  await db.insert(scores).values({ game: "limbo", handle: cleanHandle, anonId, value });
  const { rank, total, percentile } = await rankFor(db, "limbo", value);
  trackEvent(c.env, "limbo", "limbo_win", anonId, value);
  return c.json({ won: true, roll, target, value, rank, total, percentile }, 201);
});

// --- Shared events: draw + rocket. Rounds are time math; resolution is lazy ---
// (first request after a round closes performs it) and idempotent (a synthetic
// "_resolved_" PK row is the lock — racing resolvers hit the conflict and skip).

const RESOLVED_ANON = "_resolved_";

function validAnon(v: unknown): v is string {
  return typeof v === "string" && v.length >= 8 && v.length <= 64;
}

/** Resolve draw round r if closed and not yet resolved. Safe to call every request. */
async function resolveDraw(
  c: { env: GameApiEnv; req: { url: string } },
  db: Db,
  round: number,
): Promise<void> {
  if (Date.now() < drawEndsAt(round)) return;
  const digest = await hmacHex(crashSeed(c.env, c.req.url), `draw.${round}`);
  const outcome = drawOutcome(digest);

  // The lock: whoever lands this insert does the side effects.
  const lock = await db
    .insert(eventPicks)
    .values({
      game: "draw",
      roundId: round,
      anonId: RESOLVED_ANON,
      handle: "_",
      choice: -1,
      result: outcome,
    })
    .onConflictDoNothing();
  if (((lock as { meta?: { changes?: number } }).meta?.changes ?? 0) === 0) return;

  const picks = await db
    .select()
    .from(eventPicks)
    .where(and(eq(eventPicks.game, "draw"), eq(eventPicks.roundId, round)));

  for (const p of picks) {
    if (p.anonId === RESOLVED_ANON || p.result !== null) continue;
    const hit = p.choice === outcome;
    await db
      .update(eventPicks)
      .set({ result: hit ? 1 : 0 })
      .where(
        and(
          eq(eventPicks.game, "draw"),
          eq(eventPicks.roundId, round),
          eq(eventPicks.anonId, p.anonId),
        ),
      );
    if (!hit) continue;

    // Streak: consecutive WON draws (a miss resolves to a fresh 1 next win; a
    // skipped day breaks it too — lastPlay tells us which round they last won).
    const [lockRow] = await db
      .select()
      .from(dailyLocks)
      .where(and(eq(dailyLocks.anonId, p.anonId), eq(dailyLocks.game, "draw")));
    const prevWonRound = lockRow ? drawRoundAt(lockRow.lastPlay.getTime()) : null;
    const streak =
      prevWonRound === round - 1 && lockRow ? lockRow.streak + 1 : 1;
    const lastPlay = new Date(drawEndsAt(round));
    if (lockRow) {
      await db
        .update(dailyLocks)
        .set({ streak, lastPlay })
        .where(and(eq(dailyLocks.anonId, p.anonId), eq(dailyLocks.game, "draw")));
    } else {
      await db
        .insert(dailyLocks)
        .values({ anonId: p.anonId, game: "draw", streak, lastPlay });
    }
    await db
      .insert(scores)
      .values({ game: "draw", handle: p.handle, anonId: p.anonId, value: streak });
    trackEvent(c.env, "draw", "draw_hit", p.anonId, streak);
  }
}

/** Round summary for the reveal screen (winners, total, outcome, my verdict). */
async function drawLastRound(db: Db, round: number, anonId: string | null) {
  const rows = await db
    .select()
    .from(eventPicks)
    .where(and(eq(eventPicks.game, "draw"), eq(eventPicks.roundId, round)));
  const marker = rows.find((r) => r.anonId === RESOLVED_ANON);
  if (!marker) return null;
  const players = rows.filter((r) => r.anonId !== RESOLVED_ANON);
  const mine = anonId ? players.find((r) => r.anonId === anonId) : undefined;
  return {
    roundId: round,
    outcome: marker.result ?? 0,
    winners: players.filter((r) => r.result === 1).length,
    total: players.length,
    me: mine ? { picked: mine.choice, hit: mine.result === 1 } : null,
  };
}

router.get("/draw/state", async (c) => {
  const anonId = c.req.query("anonId") ?? "";
  const db = c.get("db");
  const now = Date.now();
  const round = drawRoundAt(now);
  await resolveDraw(c, db, round - 1);

  const picks = await db
    .select({ anonId: eventPicks.anonId, choice: eventPicks.choice })
    .from(eventPicks)
    .where(and(eq(eventPicks.game, "draw"), eq(eventPicks.roundId, round)));
  const players = picks.filter((p) => p.anonId !== RESOLVED_ANON);
  const distribution = Array.from({ length: DRAW_SLOTS }, () => 0);
  for (const p of players) {
    if (p.choice >= 0 && p.choice < DRAW_SLOTS) distribution[p.choice]!++;
  }
  const mine = validAnon(anonId)
    ? (players.find((p) => p.anonId === anonId)?.choice ?? null)
    : null;

  let myStreak = 0;
  if (validAnon(anonId)) {
    const [lockRow] = await db
      .select({ streak: dailyLocks.streak, lastPlay: dailyLocks.lastPlay })
      .from(dailyLocks)
      .where(and(eq(dailyLocks.anonId, anonId), eq(dailyLocks.game, "draw")));
    // A streak is only alive if the last WON round was yesterday's or today's.
    if (lockRow && drawRoundAt(lockRow.lastPlay.getTime()) >= round - 1) {
      myStreak = lockRow.streak;
    }
  }

  return c.json({
    serverNow: now,
    roundId: round,
    endsAt: drawEndsAt(round),
    locked: drawPicksLocked(now, round),
    slots: DRAW_SLOTS,
    count: players.length,
    distribution,
    myPick: mine,
    myStreak,
    last: await drawLastRound(db, round - 1, validAnon(anonId) ? anonId : null),
  });
});

router.post("/draw/pick", async (c) => {
  let body: unknown;
  try {
    body = await c.req.json();
  } catch {
    return c.json({ error: "bad json" }, 400);
  }
  const { anonId, handle, choice } = (body ?? {}) as Record<string, unknown>;
  if (!validAnon(anonId)) return c.json({ error: "bad anonId" }, 400);
  const cleanHandle = sanitizeHandle(handle);
  if (cleanHandle === null) return c.json({ error: "bad handle" }, 400);
  if (
    typeof choice !== "number" ||
    !Number.isInteger(choice) ||
    choice < 0 ||
    choice >= DRAW_SLOTS
  ) {
    return c.json({ error: "bad choice" }, 400);
  }

  const now = Date.now();
  const round = drawRoundAt(now);
  if (drawPicksLocked(now, round)) {
    return c.json({ error: "picks are locked — reveal imminent" }, 409);
  }

  const db = c.get("db");
  const res = await db
    .insert(eventPicks)
    .values({ game: "draw", roundId: round, anonId, handle: cleanHandle, choice })
    .onConflictDoNothing();
  if (((res as { meta?: { changes?: number } }).meta?.changes ?? 0) === 0) {
    return c.json({ error: "already picked this round" }, 409);
  }
  trackEvent(c.env, "draw", "draw_pick", anonId, choice);
  return c.json({ ok: true, roundId: round, choice }, 201);
});

// --- rocket: every 10 minutes, one shared flight ---

async function rocketCrashPoint(
  c: { env: GameApiEnv; req: { url: string } },
  round: number,
): Promise<number> {
  return deriveCrash(crashSeed(c.env, c.req.url), `rocket.${round}`);
}

router.get("/rocket/state", async (c) => {
  const anonId = c.req.query("anonId") ?? "";
  const db = c.get("db");
  const now = Date.now();
  const round = rocketRoundAt(now);
  const launchAt = rocketLaunchAt(round);
  const crashPoint = await rocketCrashPoint(c, round);
  const crashesAtMs = launchAt + timeToReach(crashPoint) * 1000;
  const phase = rocketPhase(now, round, crashesAtMs);

  // Lazily zero out riders of any finished flight (this round's or the last's).
  // Pure UPDATE … WHERE result IS NULL — idempotent, no lock needed.
  const zeroRound = phase === "landed" ? round : round - 1;
  await db
    .update(eventPicks)
    .set({ result: 0 })
    .where(
      and(
        eq(eventPicks.game, "rocket"),
        eq(eventPicks.roundId, zeroRound),
        sql`${eventPicks.result} is null`,
      ),
    );

  const rows = await db
    .select()
    .from(eventPicks)
    .where(and(eq(eventPicks.game, "rocket"), eq(eventPicks.roundId, round)));
  const me = validAnon(anonId) ? rows.find((r) => r.anonId === anonId) : undefined;

  const lastRows = await db
    .select()
    .from(eventPicks)
    .where(and(eq(eventPicks.game, "rocket"), eq(eventPicks.roundId, round - 1)));
  const lastCp = await rocketCrashPoint(c, round - 1);

  return c.json({
    serverNow: now,
    roundId: round,
    phase,
    launchAt,
    boardedCount: rows.length,
    meBoarded: !!me,
    myResult: me?.result ?? null,
    // The curve is server-committed; revealing it mid-flight doesn't beat the
    // server clock (cashout pays server-time multiplier), same model as crash.
    crashPoint: phase === "boarding" ? null : crashPoint,
    crashesAt: phase === "boarding" ? null : crashesAtMs,
    last:
      lastRows.length > 0
        ? {
            roundId: round - 1,
            crashPoint: lastCp,
            survivors: lastRows.filter((r) => (r.result ?? 0) > 0).length,
            riders: lastRows.filter((r) => r.result === 0).length,
            best: Math.max(...lastRows.map((r) => r.result ?? 0)),
          }
        : null,
  });
});

router.post("/rocket/board", async (c) => {
  let body: unknown;
  try {
    body = await c.req.json();
  } catch {
    return c.json({ error: "bad json" }, 400);
  }
  const { anonId, handle } = (body ?? {}) as Record<string, unknown>;
  if (!validAnon(anonId)) return c.json({ error: "bad anonId" }, 400);
  const cleanHandle = sanitizeHandle(handle);
  if (cleanHandle === null) return c.json({ error: "bad handle" }, 400);

  const now = Date.now();
  const round = rocketRoundAt(now);
  if (now >= rocketLaunchAt(round)) {
    return c.json({ error: "doors closed — flight's away" }, 409);
  }

  const db = c.get("db");
  await db
    .insert(eventPicks)
    .values({ game: "rocket", roundId: round, anonId, handle: cleanHandle, choice: 0 })
    .onConflictDoNothing(); // re-boarding is a no-op, not an error
  trackEvent(c.env, "rocket", "rocket_board", anonId);
  return c.json({ ok: true, roundId: round, launchAt: rocketLaunchAt(round) }, 201);
});

router.post("/rocket/cashout", async (c) => {
  let body: unknown;
  try {
    body = await c.req.json();
  } catch {
    return c.json({ error: "bad json" }, 400);
  }
  const { anonId, roundId } = (body ?? {}) as Record<string, unknown>;
  if (!validAnon(anonId)) return c.json({ error: "bad anonId" }, 400);
  if (typeof roundId !== "number" || !Number.isInteger(roundId)) {
    return c.json({ error: "bad round" }, 400);
  }

  const now = Date.now();
  if (roundId !== rocketRoundAt(now)) return c.json({ error: "stale round" }, 409);
  const launchAt = rocketLaunchAt(roundId);
  if (now < launchAt) return c.json({ error: "not flying yet" }, 409);

  const elapsed = (now - launchAt) / 1000;
  if (elapsed > ROCKET_MAX_FLIGHT_S) return c.json({ error: "flight over" }, 409);
  const crashPoint = await rocketCrashPoint(c, roundId);
  const serverMult = multiplierAt(elapsed);
  if (serverMult >= crashPoint) {
    return c.json({ won: false, crashPoint }, 200); // too late — it already blew
  }

  const db = c.get("db");
  const [row] = await db
    .select()
    .from(eventPicks)
    .where(
      and(
        eq(eventPicks.game, "rocket"),
        eq(eventPicks.roundId, roundId),
        eq(eventPicks.anonId, anonId),
      ),
    );
  if (!row) return c.json({ error: "you never boarded" }, 404);
  if (row.result !== null) return c.json({ error: "already cashed" }, 409);

  const value = Math.min(Math.max(toScoreValue(serverMult), CRASH_MIN), CRASH_MAX);
  const res = await db
    .update(eventPicks)
    .set({ result: value })
    .where(
      and(
        eq(eventPicks.game, "rocket"),
        eq(eventPicks.roundId, roundId),
        eq(eventPicks.anonId, anonId),
        sql`${eventPicks.result} is null`,
      ),
    );
  if (((res as { meta?: { changes?: number } }).meta?.changes ?? 0) === 0) {
    return c.json({ error: "already cashed" }, 409); // double-tap race lost
  }

  await db
    .insert(scores)
    .values({ game: "rocket", handle: row.handle, anonId, value });
  const { rank, total, percentile } = await rankFor(db, "rocket", value);
  trackEvent(c.env, "rocket", "rocket_cashout", anonId, value);
  return c.json(
    { won: true, multiplier: serverMult, value, rank, total, percentile },
    201,
  );
});

// --- Plinko: one drop, the digest IS the path ---

router.post("/plinko/drop", async (c) => {
  let body: unknown;
  try {
    body = await c.req.json();
  } catch {
    return c.json({ error: "bad json" }, 400);
  }
  const { anonId, handle } = (body ?? {}) as Record<string, unknown>;
  if (typeof anonId !== "string" || anonId.length < 8 || anonId.length > 64) {
    return c.json({ error: "bad anonId" }, 400);
  }
  const cleanHandle = sanitizeHandle(handle);
  if (cleanHandle === null) return c.json({ error: "bad handle" }, 400);
  if (await rateLimited(c.env, anonId, "plinko", CRASH_START_WINDOW_S, "rlstart")) {
    return c.json({ error: "slow down" }, 429);
  }

  // Fresh path per drop; the client replays the exact bounce sequence we rolled.
  const digest = await hmacHex(
    crashSeed(c.env, c.req.url),
    `plinko.${crypto.randomUUID()}`,
  );
  const { path, bucket, mult } = plinkoDrop(digest);

  // Sub-1x buckets are duds — no score row, just the sad bounce.
  if (mult < 1) {
    trackEvent(c.env, "plinko", "plinko_dud", anonId, Math.round(mult * 100));
    return c.json({ path, bucket, mult });
  }

  const value = plinkoToScoreValue(mult);
  const db = c.get("db");
  await db.insert(scores).values({ game: "plinko", handle: cleanHandle, anonId, value });
  const { rank, total, percentile } = await rankFor(db, "plinko", value);
  trackEvent(c.env, "plinko", "plinko_hit", anonId, value);
  return c.json({ path, bucket, mult, value, rank, total, percentile }, 201);
});

router.get("/og", (c) => {
  // /api/og is handled UPSTREAM in the app's Worker (apps/reflex/worker/index.ts),
  // which owns the imported fonts/wasm modules and renders the card. This route is only
  // reached if that interception is removed — surface that clearly.
  return c.json({ error: "og handled by the app worker, not the router" }, 501);
});

/** Rank (1-based) + percentile for a just-submitted value. "top X%" card line. */
async function rankFor(db: Db, game: GameId, value: number) {
  const dir = GAMES[game].dir;
  // "Better" count: strictly better scores rank ahead of this one.
  const betterExpr =
    dir === "asc"
      ? sql<number>`sum(case when ${scores.value} < ${value} then 1 else 0 end)`
      : sql<number>`sum(case when ${scores.value} > ${value} then 1 else 0 end)`;

  const [agg] = await db
    .select({
      total: sql<number>`count(*)`,
      better: betterExpr,
    })
    .from(scores)
    .where(eq(scores.game, game));

  const total = agg?.total ?? 1;
  const better = agg?.better ?? 0;
  const rank = better + 1;
  // top X% — smaller is better. rank 1 of N => top (1/N).
  const percentile = total > 0 ? Math.max(1, Math.ceil((rank / total) * 100)) : 100;
  return { rank, total, percentile };
}

/**
 * Per-anonId+game throttle. Uses KV if bound; otherwise no-op (local dev / not yet
 * provisioned). Abuse posture is intentionally light — see ARCHITECTURE.md.
 */
async function rateLimited(
  env: GameApiEnv,
  anonId: string,
  game: string,
  windowS: number = SCORE_RATE_WINDOW_S,
  keyPrefix: string = "rl",
): Promise<boolean> {
  const kv = env.RATE_LIMIT;
  if (!kv) return false;
  const key = `${keyPrefix}:${game}:${anonId}`;
  try {
    // KV's minimum expirationTtl is 60s — sub-minute windows can't lean on expiry.
    // The window lives in the stored timestamp; the TTL just garbage-collects the key.
    const now = Date.now();
    const last = await kv.get(key);
    if (last !== null && now - Number(last) < windowS * 1000) return true;
    await kv.put(key, String(now), { expirationTtl: Math.max(windowS, 60) });
    return false;
  } catch (e) {
    // Fail open (a down KV must not take the games down) — but say so in the logs.
    console.error("rate-limit KV error (failing open):", e);
    return false;
  }
}

// --- Crash game: provably-fair HMAC-derived crash point ---

const DEV_CRASH_SEED = "dev-crash-seed-not-for-prod";
const CRASH_START_WINDOW_S = 2; // one new round per 2s per anon
const CRASH_MAX_ROUND_MS = 120_000; // a cashout older than this is rejected as stale
const CRASH_CLOCK_SKEW_MS = 2_000; // tolerate this much client-clock-ahead

function crashSeed(env: GameApiEnv, reqUrl: string): string {
  if (env.CRASH_SEED) return env.CRASH_SEED;
  const host = new URL(reqUrl).hostname;
  const isLocal =
    host === "localhost" || host === "127.0.0.1" || host.endsWith(".localhost");
  if (isLocal) return DEV_CRASH_SEED;
  // A known seed in production = anyone can precompute every crash point. Refuse loudly
  // (Hono's default error handler turns this into a 500) rather than run rigged-but-open.
  throw new Error(
    "CRASH_SEED secret is not set on this Worker — refusing to run the crash game " +
      "with the public dev seed. Set it: wrangler secret put CRASH_SEED",
  );
}

/** HMAC-SHA256(secret, msg) → lowercase hex. */
async function hmacHex(secret: string, msg: string): Promise<string> {
  const enc = new TextEncoder();
  const key = await crypto.subtle.importKey(
    "raw",
    enc.encode(secret),
    { name: "HMAC", hash: "SHA-256" },
    false,
    ["sign"],
  );
  const sigBuf = await crypto.subtle.sign("HMAC", key, enc.encode(msg));
  return [...new Uint8Array(sigBuf)]
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

/** Constant-time-ish hex string compare. */
function safeEqual(a: string, b: string): boolean {
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i++) diff |= a.charCodeAt(i) ^ b.charCodeAt(i);
  return diff === 0;
}

/** Derive the round's crash point deterministically from the secret + roundId. The
 *  client can't predict it (no secret) and can't forge a cashout above it (we re-derive
 *  here, never trusting the client's reported crash point). */
async function deriveCrash(secret: string, roundId: string): Promise<number> {
  const digest = await hmacHex(secret, roundId);
  // first 52 bits of the digest → uniform [0,1)
  const bits = Number.parseInt(digest.slice(0, 13), 16);
  const r = bits / 2 ** 52;
  return deriveCrashFromR(r);
}
